/**
 * Single source of truth for obligation status configuration (label + color + aggregate count field names).
 * All exports are derived from STATUS_DEFINITIONS. Add or reorder statuses here only.
 *
 * Localization: use getStatusLabel(key, t) or getStatusOptions(t). When adding i18n,
 * add keys to your locale under dashboard:common.status.* (same wording as ObligationStatusCards).
 *
 * For color by display label (e.g. "Working On", "Completed Late") use getStatusColorByDisplayLabel(label)
 * or STATUS_COLORS_BY_LABEL[label] (replaces legacy utils/theme.js).
 */

/**
 * Canonical list: table order. apiValue matches backend ObligationStatus (compliance_backend/enums.py).
 *
 * labelKey / default `label` for all statuses point to dashboard:common.status.* so every component
 * (filter sidebar, DashboardObligationsTable, ObligationStatusCards, FrameworkComplianceDetails, etc.)
 * resolves status labels from the same canonical owner.
 */
const STATUS_DEFINITIONS = [
  { key: "completed", apiValue: "completed", statusCountsKey: "completedCount", label: "Completed", labelKey: "common.status.completed", color: "#297A2B" },
  { key: "completedLate", apiValue: "completed late", statusCountsKey: "completedLateCount", label: "Completed late", labelKey: "common.status.completed_late", color: "#C7842C" },
  { key: "late", apiValue: "late", statusCountsKey: "lateCount", label: "Overdue", labelKey: "common.status.late", color: "#852A2E" },
  { key: "inProgress", apiValue: "working on", statusCountsKey: "workingOnCount", label: "Work in progress", labelKey: "common.status.in_progress", color: "#093E7D" },
  { key: "scheduled", apiValue: "scheduled", statusCountsKey: "scheduledCount", label: "Scheduled", labelKey: "common.status.scheduled", color: "#999999" },
];

/**
 * Default order status keys array
 */
export const STATUS_KEYS = STATUS_DEFINITIONS.map((s) => s.key);

/**
 * Status order used by the filter sidebar
 * 'Completed late' before 'Late' reads better
 */
export const FILTER_SIDEBAR_STATUS_KEYS_ORDER = ["scheduled", "inProgress", "completed", "completedLate", "late"];

// -----------------------------------------------------------------------------
// By key lookups for the different properties of a status object
// -----------------------------------------------------------------------------

export const STATUS_CONFIG = Object.fromEntries(
  STATUS_DEFINITIONS.map((s) => [s.key, { label: s.label, labelKey: s.labelKey, color: s.color }])
);

export const STATUS_COLORS = Object.fromEntries(STATUS_DEFINITIONS.map((s) => [s.key, s.color]));

/** Default English labels (use getStatusLabel(key, t) when i18n is active). */
export const STATUS_LABELS = Object.fromEntries(STATUS_DEFINITIONS.map((s) => [s.key, s.label]));

// -----------------------------------------------------------------------------
// Derived API values and API object lookups
// -----------------------------------------------------------------------------
const API_VALUE_SET = new Set(STATUS_DEFINITIONS.map((s) => s.apiValue.toLowerCase()));
const API_VALUE_TO_DEFINITION = Object.fromEntries(STATUS_DEFINITIONS.map((s) => [s.apiValue, s]));
const LABEL_TO_API_VALUE = Object.fromEntries(STATUS_DEFINITIONS.map((s) => [s.label.toLowerCase(), s.apiValue]));

/** Legacy / display aliases accepted during migration (all keys must be lowercase). */
const STATUS_ALIASES_TO_API_VALUE = {
  completed_late: "completed late",
  completedlate: "completed late",
  "comp. late": "completed late",
  "completed-late": "completed late",
  in_progress: "working on",
  inprogress: "working on",
  working_on: "working on",
  "working-on": "working on",
  // Old UI string "In progress" still normalizes to backend "working on" after label → "Work in progress"
  "in progress": "working on",
};

/** Status definition key → filter/API value (backend ObligationStatus). */
export const STATUS_KEY_TO_API_VALUE = Object.fromEntries(STATUS_DEFINITIONS.map((s) => [s.key, s.apiValue]));

/** Reverse of STATUS_KEY_TO_API_VALUE: apiValue → definition key (e.g. "working on" → "inProgress"). */
export const STATUS_API_VALUE_TO_KEY = Object.fromEntries(STATUS_DEFINITIONS.map((s) => [s.apiValue, s.key]));

/** UI status values in table order (for dropdown "ALL" = this list). */
export const STATUS_TABLE_VALUES = STATUS_DEFINITIONS.map((s) => s.apiValue);

/**
 * Normalize one status input to canonical backend ObligationStatus values.
 * Accepts:
 * - canonical values ("working on", "completed late", ...)
 * - legacy filter tokens ("in_progress", "completed_late")
 * - UI labels ("In progress", "Completed late")
 * - definition keys ("inProgress", "completedLate")
 *
 * @param {string} value
 * @returns {string} canonical api value or empty string if unknown
 */
export function normalizeObligationStatusValue(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  if (STATUS_KEY_TO_API_VALUE[raw]) return STATUS_KEY_TO_API_VALUE[raw];

  const lower = raw.toLowerCase();
  if (API_VALUE_SET.has(lower)) return lower;
  if (STATUS_ALIASES_TO_API_VALUE[lower]) return STATUS_ALIASES_TO_API_VALUE[lower];
  if (LABEL_TO_API_VALUE[lower]) return LABEL_TO_API_VALUE[lower];

  const collapsed = lower.replace(/[\s._-]+/g, "_");
  if (STATUS_ALIASES_TO_API_VALUE[collapsed]) return STATUS_ALIASES_TO_API_VALUE[collapsed];

  return "";
}

/**
 * Normalize a list of status values to canonical backend values.
 * Preserves order and removes duplicates/unknown values.
 */
export function normalizeObligationStatuses(statuses) {
  if (!Array.isArray(statuses)) return [];
  const out = [];
  const seen = new Set();
  statuses.forEach((status) => {
    const normalized = normalizeObligationStatusValue(status);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push(normalized);
  });
  return out;
}

/** Dashboard table API: all ObligationStatus values when no status filter. */
export const DASHBOARD_TABLE_API_STATUSES = [...STATUS_TABLE_VALUES];

/**
 * Map filter-store / sidebar status strings to backend POST payloads (dashboard widgets, files APIs).
 * Aligns with `ObligationStatus` in compliance_backend/enums.py.
 *
 * @param {string[]} statuses
 * @returns {string[]}
 */
export function mapFilterStatusesForBackendApi(statuses) {
  return normalizeObligationStatuses(statuses);
};

/** Status definition key (e.g. completedLate) → `statusCounts` property. */
export const STATUS_KEY_TO_COUNT_KEY = Object.fromEntries(
  STATUS_DEFINITIONS.map((s) => [s.key, s.statusCountsKey])
);

/** Filter-store / sidebar apiValue → `statusCounts` property (groups, hierarchy rows, StatusBadgeV2). */
export const STATUS_API_VALUE_TO_COUNT_KEY = Object.fromEntries(
  STATUS_DEFINITIONS.map((s) => [s.apiValue, s.statusCountsKey])
);

// -----------------------------------------------------------------------------
// Derived: options arrays
// -----------------------------------------------------------------------------

/** Filter sidebar options (id/value = apiValue for API). Use getStatusOptions(t) for translated labels. */
export const STATUS_OPTIONS = FILTER_SIDEBAR_STATUS_KEYS_ORDER.map((key) => {
  const s = STATUS_DEFINITIONS.find((d) => d.key === key);
  return s ? { id: s.apiValue, label: s.label, value: s.apiValue, color: s.color } : null;
}).filter(Boolean);

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Accent / pill color for a status (same hex as ObligationStatusCards `style={{ "--status-color" }}` and accent bar).
 * @param {string} apiValue - Filter/API value (e.g. `"working on"`, `"completed late"`) or legacy alias
 * @returns {string|null} Hex color or null if unknown
 */
export function getStatusAccentColor(apiValue) {
  const normalized = normalizeObligationStatusValue(apiValue);
  if (!normalized) return null;
  const def = API_VALUE_TO_DEFINITION[normalized];
  return def ? def.color : null;
};

/**
 * Compute the next selection state for statuses
 * Clicking "_total", if included in the options, always resets the selection
 * Clicking an active status removes it and clicking an inactive one adds it
 * Deselecting every status falls back to ["_total"] if it exists in the options, otherwise an empty array
 *
 * @param {string[]} current - Currently selected keys
 * @param {string} key - The key that was clicked
 * @returns {string[]}
 */
export function getStatusSelectionState(current, key, availableKeys = []) {
  const hasTotalOption = availableKeys.includes("_total");

  if (key === "_total") return hasTotalOption ? ["_total"] : [];

  const withoutTotal = current.filter((k) => k !== "_total");
  const toggled = withoutTotal.includes(key)
    ? withoutTotal.filter((k) => k !== key)
    : [...withoutTotal, key];

  if (!toggled.length) return hasTotalOption ? ["_total"] : [];
  return toggled;
};

/**
 * Get translated label for a status key. Use when i18n is enabled.
 * @param {string} key - Status key (e.g. "completed", "completedLate")
 * @param {function} t - i18n translate function (e.g. useTranslation().t)
 * @returns {string} Translated label, or default English if t is not provided
 */
export function getStatusLabel(key, t) {
  const config = STATUS_CONFIG[key];
  if (!config) return "";
  if (typeof t === "function") return t(config.labelKey, { ns: "dashboard" });
  return config.label;
};

/**
 * Get translated label for a status api value (canonical or legacy alias).
 * @param {string} value - e.g. "working on", "in_progress"
 * @param {function} t
 * @returns {string}
 */
export function getStatusLabelByApiValue(value, t) {
  const normalized = normalizeObligationStatusValue(value);
  if (!normalized) return "";
  const def = API_VALUE_TO_DEFINITION[normalized];
  if (!def) return normalized;
  if (typeof t === "function") return t(def.labelKey, { ns: "dashboard" });
  return def.label;
};

/**
 * Filter sidebar STATUS section: translated labels + api values for DynamicFilterSidebar CheckboxList.
 * Uses each row’s `labelKey` (status.* and cards.late / cards.working_on — same keys as ObligationStatusCards).
 *
 * @param {function} t - i18n translate function
 * @returns {Array<{ id: string, label: string, value: string, color: string }>}
 */
export function getStatusOptions(t) {
  if (typeof t !== "function") return STATUS_OPTIONS;
  return FILTER_SIDEBAR_STATUS_KEYS_ORDER.map((key) => {
    const config = STATUS_CONFIG[key];
    const value = STATUS_KEY_TO_API_VALUE[key];
    return {
      id: value,
      label: t(config.labelKey, { ns: "dashboard" }),
      value,
      color: config.color,
    };
  });
}

/**
 * Dashboard obligations table status dropdown: same labels as ObligationStatusCards / filter sidebar
 * (explicit `{ ns: "dashboard" }` so keys like cards.late resolve even if default i18n ns differs).
 *
 * @param {function} [t] - i18n `t`; falls back to English `label` from STATUS_DEFINITIONS
 * @returns {Array<{ key: string, value: string, label: string, color: string }>}
 */
export function getStatusOptionsTableOrder(t) {
  return STATUS_DEFINITIONS.map((s) => ({
    key: s.key,
    value: s.apiValue,
    // Must use dashboard namespace so cards.* and status.* keys match ObligationStatusCards
    label: typeof t === "function" ? t(s.labelKey, { ns: "dashboard" }) : s.label,
    color: s.color,
  }));
};

// -----------------------------------------------------------------------------
// Color by display label (replaces legacy theme.js STATUS_COLORS)
// -----------------------------------------------------------------------------

/** Color lookup by display label (title case, lowercase, "Comp. Late" etc.). Use when you have a status string from API/UI. */
export const STATUS_COLORS_BY_LABEL = (() => {
  const out = {};
  STATUS_DEFINITIONS.forEach((s) => {
    out[s.label] = s.color;
    out[s.label.toLowerCase()] = s.color;
    out[s.apiValue] = s.color;
    out[s.apiValue.toLowerCase()] = s.color;
  });
  Object.entries(STATUS_ALIASES_TO_API_VALUE).forEach(([alias, apiValue]) => {
    const def = API_VALUE_TO_DEFINITION[apiValue];
    if (def) out[alias] = def.color;
  });
  out["Working On"] = STATUS_COLORS.inProgress;
  out["Comp. Late"] = STATUS_COLORS.completedLate;
  // API/UI may still emit old phrasing; keep colors stable after label → Overdue / Work in progress
  out.Late = STATUS_COLORS.late;
  out["In progress"] = STATUS_COLORS.inProgress;
  return out;
})();

/**
 * Get status color from a display label or variant (e.g. "Working On", "completed late", "Comp. Late").
 * @param {string} label - Display label or API-style status
 * @param {string} [fallback="#999999"] - Color when label is unknown
 * @returns {string} Hex color
 */
export function getStatusColorByDisplayLabel(label, fallback = "#999999") {
  if (label == null || String(label).trim() === "") return fallback;
  const s = String(label).trim();
  return STATUS_COLORS_BY_LABEL[s] ?? STATUS_COLORS_BY_LABEL[s.toLowerCase()] ?? fallback;
};

// -----------------------------------------------------------------------------
// Excel export
// -----------------------------------------------------------------------------

/** Column order used by all Excel exporters. Matches statusBreakdown key names. */
export const EXCEL_STATUS_ORDER = ["late", "inProgress", "completedLate", "completed", "scheduled"];

/** English fallback labels for Excel export (when i18n is unavailable at call site). Same values as STATUS_LABELS. */
export const EXCEL_STATUS_LABELS_FALLBACK = STATUS_LABELS;